import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  User,
  ShoppingCart,
  MapPin,
  Headset,
  ShieldCheck,
  Truck,
  Lock,
  Menu,
  Plus,
  Package,
  FileText,
  LogOut,
} from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { allProducts, categories } from "../mock";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const Logo = () => (
  <Link to="/" className="flex items-center shrink-0">
    <img
      src="/logo.png"
      alt="ArogyaMeds - Trusted Medicine, Better Health"
      className="h-11 w-auto object-contain"
    />
  </Link>
);

const SearchBar = ({ placeholder }) => {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useShop();
  const matches = q.trim()
    ? allProducts
        .filter(
          (p) =>
            p.name.toLowerCase().includes(q.toLowerCase()) ||
            p.brand.toLowerCase().includes(q.toLowerCase())
        )
        .slice(0, 6)
    : [];

  return (
    <div className="relative w-full">
      <div className="flex items-center w-full border border-gray-300 rounded-full overflow-hidden bg-white focus-within:border-[#12a150] focus-within:ring-2 focus-within:ring-[#12a150]/20 transition-shadow">
        <Search className="ml-4 text-gray-400 shrink-0" size={18} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setTimeout(() => setFocus(false), 200)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2.5 text-[13px] outline-none bg-transparent placeholder:text-gray-400"
        />
        <button
          onClick={() => matches.length && navigate(`/product/${matches[0].id}`)}
          className="bg-[#12a150] hover:bg-[#0d8a40] text-white text-[13px] font-semibold px-6 lg:px-8 py-2.5 transition-colors"
        >
          Search
        </button>
      </div>
      {focus && q.trim() && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white border border-gray-100 rounded-xl shadow-xl z-40 overflow-hidden">
          {matches.length === 0 ? (
            <div className="p-4 text-[13px] text-gray-500">
              No products found for "{q}"
            </div>
          ) : (
            matches.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 cursor-pointer"
                onMouseDown={() => navigate(`/product/${p.id}`)}
              >
                <img src={p.img} alt={p.name} className="w-9 h-9 rounded-md object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-800 truncate">{p.name}</div>
                  <div className="text-[11px] text-gray-400">
                    {p.brand} · <span className="text-[#0e7a3d] font-semibold">₹{p.price}</span>
                  </div>
                </div>
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    addToCart(p);
                  }}
                  className="w-7 h-7 rounded-full bg-[#12a150] hover:bg-[#0d8a40] text-white flex items-center justify-center transition-colors"
                >
                  <Plus size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const IconItem = ({ icon: Icon, lines, onClick, badge, accent }) => (
  <button
    onClick={onClick}
    className="relative flex flex-col items-center gap-1 text-gray-700 hover:text-[#0e7a3d] transition-colors min-w-[52px]"
  >
    <span className="relative">
      <Icon size={22} strokeWidth={1.8} />
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-2 bg-[#12a150] text-white text-[9px] font-bold min-w-[16px] h-4 px-0.5 rounded-full flex items-center justify-center">
          {badge}
        </span>
      )}
    </span>
    <span className="text-[10.5px] font-medium leading-[1.15] text-center whitespace-nowrap">
      {lines[0]}
      {lines[1] && (
        <>
          <br />
          <span className={accent ? "text-[#0e7a3d] font-semibold" : ""}>{lines[1]}</span>
        </>
      )}
    </span>
  </button>
);

const Divider = () => <div className="hidden sm:block h-9 w-px bg-gray-200" />;

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const { setModal } = useShop();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="lg:hidden p-1 text-gray-700">
          <Menu size={24} />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[290px] p-0">
        <div className="bg-[#0f9d4b] px-4 py-4">
          <div className="bg-white rounded-xl px-3 py-2 w-fit">
            <Logo />
          </div>
        </div>
        <div className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-90px)]">
          <Link to="/" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-[14px] font-semibold text-[#0e7a3d] bg-green-50">
            Home
          </Link>
          <button
            onClick={() => {
              setOpen(false);
              setModal("upload");
            }}
            className="w-full text-left px-3 py-2.5 rounded-lg text-[14px] font-medium text-gray-700 hover:bg-gray-50"
          >
            Upload Prescription
          </button>
          <button
            onClick={() => {
              setOpen(false);
              setModal("track");
            }}
            className="w-full text-left px-3 py-2.5 rounded-lg text-[14px] font-medium text-gray-700 hover:bg-gray-50"
          >
            Track Order
          </button>
          <div className="pt-2 pb-1 px-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            Shop by Category
          </div>
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-[13.5px] font-medium text-gray-700 hover:bg-green-50 hover:text-[#0e7a3d]"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Header = ({ variant = "home" }) => {
  const { cartCount, cartTotal, setCartOpen, setModal } = useShop();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const userLines = user
    ? [`Hi, ${user.name.split(" ")[0]}`, "Account"]
    : ["Login /", "Register"];

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
      <div className="max-w-[1400px] mx-auto px-3 lg:px-4 py-3 flex items-center gap-3 lg:gap-6">
        <MobileMenu />
        <Logo />
        <div className="hidden md:block flex-1 max-w-[520px] mx-auto">
          <SearchBar
            placeholder={
              variant === "home"
                ? "Search for medicines, brands, health products..."
                : "Search for medicines, health products..."
            }
          />
        </div>
        <div className="ml-auto flex items-center gap-3 sm:gap-4 lg:gap-5">
          {variant === "home" ? (
            <>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div>
                      <IconItem icon={User} lines={userLines} accent onClick={() => {}} />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuLabel className="text-[12px]">
                      <div className="font-semibold text-gray-800 truncate">{user.name}</div>
                      <div className="text-[11px] text-gray-400 truncate font-normal">{user.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/orders")} className="cursor-pointer text-[13px]">
                      <Package size={15} className="mr-2 text-[#12a150]" /> My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setModal("upload")} className="cursor-pointer text-[13px]">
                      <FileText size={15} className="mr-2 text-[#12a150]" /> Upload Prescription
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-[13px] text-red-600 focus:text-red-600">
                      <LogOut size={15} className="mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <IconItem icon={User} lines={userLines} onClick={() => setModal("login")} />
              )}
              <Divider />
              <IconItem
                icon={ShoppingCart}
                lines={["My Cart", `₹ ${cartTotal.toFixed(2)}`]}
                badge={cartCount}
                accent
                onClick={() => setCartOpen(true)}
              />
              <Divider />
              <div className="hidden sm:block">
                <IconItem icon={MapPin} lines={["Track", "Order"]} onClick={() => setModal("track")} />
              </div>
              <Divider />
              <div className="hidden sm:block">
                <IconItem
                  icon={Headset}
                  lines={["24/7", "Support"]}
                  onClick={() => window.dispatchEvent(new Event("open-chat"))}
                />
              </div>
            </>
          ) : (
            <>
              <IconItem icon={ShieldCheck} lines={["Genuine", "Products"]} onClick={() => {}} />
              <Divider />
              <IconItem icon={Truck} lines={["Fast", "Delivery"]} onClick={() => {}} />
              <Divider />
              <div className="hidden sm:block">
                <IconItem icon={Lock} lines={["Secure", "Payments"]} onClick={() => {}} />
              </div>
              <Divider />
              <div className="hidden sm:block">
                <IconItem
                  icon={Headset}
                  lines={["24/7", "Support"]}
                  onClick={() => window.dispatchEvent(new Event("open-chat"))}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="md:hidden px-3 pb-3">
        <SearchBar placeholder="Search for medicines, brands, health products..." />
      </div>
    </header>
  );
};

export default Header;
